import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../../../../components/feature/Header';
import Footer from '../../../home/components/Footer';
import { ShareModal } from '../../../../components/base/ShareModal';
import ReportModal from '../../../../components/base/ReportModal';
import { AuthModal } from '../../../../components/feature/AuthModal';
import { useAuth } from '../../../../hooks/useAuth';
import { mockDiscussions, discussionCategories, getDiscussionsByCategory } from '../../../../mocks/discussions';
import { createDiscussionUrl } from '../../../../hooks/useNavigation';
import ReputationLevelBadge from '../../../../components/base/ReputationLevelBadge';
import ToolRatingsSection from '../components/ToolRatingsSection';
import TutorialSection from '../components/TutorialSection';

interface MockComment {
  id: string;
  content: string;
  author: { username: string; avatar: string; reputation: number };
  createdAt: string;
  likes: number;
}

const aiDramaComments: Record<string, MockComment[]> = {
  'ai-001': [
    { id: 'aic1-1', content: 'Great comparison! I switched from Sora to Runway specifically for the character consistency feature. It saves hours of re-generation.', author: { username: 'Drama Maker', avatar: 'https://public.readdy.ai/ai/img_res/7f16294c049f22a7fe445c666f14a5f1.jpg', reputation: 345 }, createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(), likes: 42 },
    { id: 'aic1-2', content: 'Sora 2 with native audio is a game-changer though. No more syncing issues with separate voice tracks!', author: { username: 'Audio First', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Arab%20woman%20with%20enthusiastic%20expression%20wearing%20headphones%20studio%20lighting%20clean%20background&width=80&height=80&seq=aic1b&orientation=squarish', reputation: 278 }, createdAt: new Date(Date.now() - 20 * 60 * 1000).toISOString(), likes: 31 },
    { id: 'aic1-3', content: 'Has anyone tried combining both? I use Runway for visuals and Sora for dialogue scenes. Best of both worlds!', author: { username: 'Hybrid Creator', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20innovative%20expression%20wearing%20tech%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=aic1c&orientation=squarish', reputation: 412 }, createdAt: new Date(Date.now() - 10 * 60 * 1000).toISOString(), likes: 56 },
  ],
  'ai-002': [
    { id: 'aic2-1', content: 'Lesson #3 about character consistency hit home. I spent 2 weeks on my first project just trying to keep my main character looking the same!', author: { username: 'Newbie Creator', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Arab%20woman%20with%20determined%20expression%20wearing%20casual%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=aic2a&orientation=squarish', reputation: 89 }, createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), likes: 67 },
    { id: 'aic2-2', content: 'This post should be pinned forever. Every new AI drama creator needs to read this before starting.', author: { username: 'Veteran Producer', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20experienced%20expression%20wearing%20professional%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=aic2b&orientation=squarish', reputation: 567 }, createdAt: new Date(Date.now() - 45 * 60 * 1000).toISOString(), likes: 89 },
  ],
  'ai-004': [
    { id: 'aic4-1', content: 'The LoRA training tip alone is worth gold. My consistency went from 60% to 95% after following your workflow!', author: { username: 'LoRA Convert', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Middle%20Eastern%20man%20with%20grateful%20expression%20wearing%20creative%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=aic4a&orientation=squarish', reputation: 234 }, createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), likes: 78 },
  ],
  'ai-007': [
    { id: 'aic7-1', content: 'Under $500?! I spent $2000 on my first series. Wish I had this guide earlier. Bookmarked for my next project!', author: { username: 'Budget Learner', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20young%20Arab%20woman%20with%20surprised%20expression%20wearing%20casual%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=aic7a&orientation=squarish', reputation: 156 }, createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), likes: 123 },
    { id: 'aic7-2', content: 'Pro tip: Runway has a free tier that gives you enough credits for about 2 episodes if you plan carefully.', author: { username: 'Free Tier Pro', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20Middle%20Eastern%20man%20with%20helpful%20expression%20wearing%20casual%20attire%20studio%20lighting%20clean%20background&width=80&height=80&seq=aic7b&orientation=squarish', reputation: 289 }, createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), likes: 45 },
  ],
};

export default function AiDramasDetailPage() {
  const { urlParam } = useParams<{ urlParam: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [post, setPost] = useState<typeof mockDiscussions[0] | null>(null);
  const [comments, setComments] = useState<MockComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'discussion' | 'tools' | 'tutorials'>('discussion');
  const [userVote, setUserVote] = useState<'up' | 'down' | null>(null);
  const [localUpvotes, setLocalUpvotes] = useState(0);
  const [localDownvotes, setLocalDownvotes] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  useEffect(() => {
    if (!urlParam) {
      navigate('/forum?tab=ai-dramas');
      return;
    }

    const found = mockDiscussions.find((d) => {
      if (d.category !== 'ai-dramas') return false;
      if (urlParam === d.id || urlParam === d.slug) return true;
      if (urlParam.startsWith(d.id + '-')) return true;
      if (urlParam.startsWith(d.slug)) return true;
      return false;
    });

    if (found) {
      setPost(found);
      setLocalUpvotes(found.upvotes);
      setLocalDownvotes(found.downvotes);
      setComments(aiDramaComments[found.id] || []);
      document.title = `${found.title} - AI Dramas | DramaHub Forum`;
    }
    setLoading(false);
  }, [urlParam]);

  const relatedPosts = useMemo(() => {
    if (!post) return [];
    return getDiscussionsByCategory('ai-dramas')
      .filter((d) => d.id !== post.id)
      .slice(0, 5);
  }, [post]);

  const handleVote = (type: 'up' | 'down') => {
    if (!user) { setShowAuthModal(true); return; }
    if (userVote === type) {
      setUserVote(null);
      if (type === 'up') setLocalUpvotes((v) => v - 1);
      else setLocalDownvotes((v) => v - 1);
    } else {
      if (userVote === 'up') setLocalUpvotes((v) => v - 1);
      if (userVote === 'down') setLocalDownvotes((v) => v - 1);
      setUserVote(type);
      if (type === 'up') setLocalUpvotes((v) => v + 1);
      else setLocalDownvotes((v) => v + 1);
    }
  };

  const handleAddComment = () => {
    if (!user) { setShowAuthModal(true); return; }
    if (!newComment.trim()) return;
    const c: MockComment = {
      id: `new-${Date.now()}`,
      content: newComment.trim(),
      author: { username: user.email?.split('@')[0] || 'You', avatar: '', reputation: 0 },
      createdAt: new Date().toISOString(),
      likes: 0,
    };
    setComments((prev) => [c, ...prev]);
    setNewComment('');
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleBackToForum = () => navigate('/forum?tab=ai-dramas');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="bg-white rounded-lg p-6 space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-orange-100 rounded-full">
            <i className="ri-robot-line text-3xl text-orange-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Post Not Found</h1>
          <p className="text-gray-500 mb-6">This AI Dramas post doesn&apos;t exist or has been removed.</p>
          <button onClick={handleBackToForum} className="inline-flex items-center px-5 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line mr-2"></i>Back to AI Dramas
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const netVotes = localUpvotes - localDownvotes;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <nav className="flex items-center space-x-2 text-sm text-white/70 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/forum" className="hover:text-white transition-colors">Forum</Link>
            <i className="ri-arrow-right-s-line"></i>
            <button onClick={handleBackToForum} className="hover:text-white transition-colors cursor-pointer">AI Dramas</button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white truncate max-w-[250px]">{post.title}</span>
          </nav>

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-robot-line text-2xl"></i>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold leading-tight mb-2">{post.title}</h1>
              <div className="flex items-center flex-wrap gap-3 text-sm text-white/80">
                <span className="flex items-center gap-1.5">
                  <img src={post.author.avatar} alt={post.author.username} className="w-5 h-5 rounded-full object-cover" />
                  {post.author.username}
                </span>
                <span>•</span>
                <span>{formatTimeAgo(post.createdAt)}</span>
                <span>•</span>
                <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{post.views.toLocaleString()} views</span>
                <span>•</span>
                <span className="flex items-center gap-1"><i className="ri-message-3-line"></i>{comments.length} comments</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <button
          onClick={handleBackToForum}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Back to AI Dramas
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-[64px] z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1">
            {[
              { id: 'discussion' as const, label: 'Discussion', icon: 'ri-chat-3-line' },
              { id: 'tools' as const, label: 'Tools & Ratings', icon: 'ri-tools-line' },
              { id: 'tutorials' as const, label: 'Tutorials', icon: 'ri-graduation-cap-line' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {activeTab === 'discussion' && (
              <>
                {/* Post Content */}
                <article className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
                  <div className="p-6">
                    {/* Badges */}
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      {post.isPinned && (
                        <span className="inline-flex items-center px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                          <i className="ri-pushpin-fill mr-1"></i>Pinned
                        </span>
                      )}
                      {post.tags.slice(0, 5).map((tag, i) => (
                        <span key={i} className="px-2.5 py-1 bg-orange-50 text-orange-600 text-xs rounded-full font-medium">
                          #{tag}
                        </span>
                      ))}
                    </div>

                    {/* Author */}
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <Link to={`/user/${post.author.id}`}>
                          <img src={post.author.avatar} alt={post.author.username} className="w-11 h-11 rounded-full object-cover border-2 border-gray-100 hover:border-orange-300 transition-colors" />
                        </Link>
                        <div>
                          <Link to={`/user/${post.author.id}`} className="font-semibold text-gray-900 text-sm hover:text-orange-600 transition-colors">
                            {post.author.username}
                          </Link>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <ReputationLevelBadge points={post.author.reputation} size="xs" />
                            <span>•</span>
                            <span>{formatTimeAgo(post.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="prose max-w-none mb-6">
                      <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-[15px]">{post.content}</div>
                    </div>

                    {/* Votes & Actions */}
                    <div className="flex items-center justify-between pt-5 border-t border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center bg-gray-50 rounded-lg overflow-hidden">
                          <button onClick={() => handleVote('up')} className={`flex items-center gap-1 px-3 py-2 text-sm transition-colors cursor-pointer ${userVote === 'up' ? 'bg-green-100 text-green-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                            <i className="ri-arrow-up-line"></i><span className="font-medium">{localUpvotes}</span>
                          </button>
                          <div className="w-px h-6 bg-gray-200"></div>
                          <button onClick={() => handleVote('down')} className={`flex items-center gap-1 px-3 py-2 text-sm transition-colors cursor-pointer ${userVote === 'down' ? 'bg-red-100 text-red-700' : 'text-gray-500 hover:bg-gray-100'}`}>
                            <i className="ri-arrow-down-line"></i><span className="font-medium">{localDownvotes}</span>
                          </button>
                        </div>
                        <span className={`text-sm font-semibold ${netVotes > 0 ? 'text-green-600' : netVotes < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {netVotes > 0 ? '+' : ''}{netVotes} score
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setBookmarked(!bookmarked)} className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors cursor-pointer ${bookmarked ? 'bg-orange-50 text-orange-600' : 'text-gray-500 hover:bg-gray-100'}`}>
                          <i className={bookmarked ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
                          <span className="hidden sm:inline">{bookmarked ? 'Saved' : 'Save'}</span>
                        </button>
                        <button onClick={() => setShowShareModal(true)} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
                          <i className="ri-share-line"></i><span className="hidden sm:inline">Share</span>
                        </button>
                        <button onClick={() => setShowReportModal(true)} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer">
                          <i className="ri-flag-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </article>

                {/* Comments */}
                <section className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="p-6 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900">
                      <i className="ri-chat-3-line mr-2"></i>Comments ({comments.length})
                    </h2>
                  </div>

                  <div className="p-6 border-b border-gray-100">
                    {user ? (
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm font-medium">{user.email?.charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="flex-1">
                          <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Share your thoughts on AI drama creation..."
                            rows={3}
                            maxLength={500}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-300 resize-none text-sm"
                          />
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-400">{newComment.length}/500</span>
                            <button onClick={handleAddComment} disabled={!newComment.trim()} className="px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap">
                              Post Comment
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-gray-50 rounded-lg">
                        <p className="text-gray-500 text-sm mb-3">Sign in to join the discussion</p>
                        <button onClick={() => setShowAuthModal(true)} className="px-5 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                          Sign In
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="divide-y divide-gray-50">
                    {comments.length > 0 ? comments.map((comment) => (
                      <div key={comment.id} className="p-6 hover:bg-gray-50/50 transition-colors">
                        <div className="flex items-start gap-3">
                          {comment.author.avatar ? (
                            <img src={comment.author.avatar} alt={comment.author.username} className="w-9 h-9 rounded-full object-cover" />
                          ) : (
                            <div className="w-9 h-9 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-sm font-medium">{comment.author.username.charAt(0).toUpperCase()}</span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="font-medium text-gray-900 text-sm">{comment.author.username}</span>
                              {comment.author.reputation > 0 && <ReputationLevelBadge points={comment.author.reputation} size="xs" />}
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-400">{formatTimeAgo(comment.createdAt)}</span>
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed mb-2">{comment.content}</p>
                            <div className="flex items-center gap-4 text-xs text-gray-400">
                              <button className="flex items-center gap-1 hover:text-red-500 transition-colors cursor-pointer">
                                <i className="ri-heart-line"></i><span>{comment.likes}</span>
                              </button>
                              <button className="hover:text-gray-600 transition-colors cursor-pointer">Reply</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="p-8 text-center">
                        <i className="ri-chat-3-line text-4xl text-gray-200 mb-3"></i>
                        <p className="text-gray-400 text-sm">No comments yet. Be the first to share your thoughts!</p>
                      </div>
                    )}
                  </div>
                </section>
              </>
            )}

            {activeTab === 'tools' && <ToolRatingsSection />}
            {activeTab === 'tutorials' && <TutorialSection />}
          </div>

          {/* Sidebar */}
          <aside className="w-80 flex-shrink-0 hidden lg:block">
            <div className="space-y-5 sticky top-[130px]">
              {/* Quick Stats */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-3">
                  <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                    <i className="ri-bar-chart-box-line"></i>AI Dramas Stats
                  </h3>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Total Posts</span>
                    <span className="text-lg font-bold text-gray-900">512</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Active Creators</span>
                    <span className="text-lg font-bold text-gray-900">7,234</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Tools Reviewed</span>
                    <span className="text-lg font-bold text-gray-900">8</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Tutorials</span>
                    <span className="text-lg font-bold text-gray-900">6</span>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <h3 className="font-semibold text-gray-900 text-sm mb-3">Quick Navigation</h3>
                <div className="space-y-1">
                  {[
                    { label: 'Tools & Ratings', tab: 'tools' as const, icon: 'ri-tools-line' },
                    { label: 'Tutorials', tab: 'tutorials' as const, icon: 'ri-graduation-cap-line' },
                    { label: 'Discussion', tab: 'discussion' as const, icon: 'ri-chat-3-line' },
                  ].map((item) => (
                    <button
                      key={item.tab}
                      onClick={() => setActiveTab(item.tab)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors cursor-pointer ${
                        activeTab === item.tab ? 'bg-orange-50 text-orange-600 font-medium' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <i className={item.icon}></i>{item.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-3">
                    <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                      <i className="ri-article-line"></i>Related Posts
                    </h3>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {relatedPosts.map((rp) => (
                      <Link
                        key={rp.id}
                        to={createDiscussionUrl('ai-dramas', rp.id, rp.title)}
                        className="block px-5 py-3.5 hover:bg-gray-50 transition-colors group"
                      >
                        <h4 className="text-sm font-medium text-gray-900 group-hover:text-orange-600 leading-snug line-clamp-2 mb-1.5">
                          {rp.title}
                        </h4>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <img src={rp.author.avatar} alt={rp.author.username} className="w-4 h-4 rounded-full object-cover" />
                            <span className="text-xs text-gray-500 truncate max-w-[90px]">{rp.author.username}</span>
                          </div>
                          <div className="flex items-center gap-2.5 text-xs text-gray-400">
                            <span className="flex items-center gap-0.5"><i className="ri-arrow-up-line"></i>{rp.upvotes}</span>
                            <span className="flex items-center gap-0.5"><i className="ri-message-3-line"></i>{rp.commentCount}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div className="px-5 py-3 border-t border-gray-100">
                    <button onClick={handleBackToForum} className="w-full text-center text-sm font-medium text-orange-500 hover:underline cursor-pointer whitespace-nowrap">
                      View all AI Dramas posts →
                    </button>
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl p-5 text-white">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <i className="ri-lightbulb-flash-line"></i>Start Creating
                </h3>
                <p className="text-sm text-white/80 mb-4">
                  Ready to create your own AI drama? Check out our beginner tutorials and tool recommendations.
                </p>
                <button
                  onClick={() => setActiveTab('tutorials')}
                  className="w-full py-2 bg-white text-orange-600 text-sm font-medium rounded-lg hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Browse Tutorials
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Footer />

      {showShareModal && (
        <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} title={post.title} url={window.location.href} />
      )}
      {showReportModal && (
        <ReportModal isOpen={showReportModal} onClose={() => setShowReportModal(false)} contentType="post" contentId={post.id} reportedUserId={post.author.id} />
      )}
      {showAuthModal && (
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} defaultMode="signin" />
      )}
    </div>
  );
}
